import { loadStripe, StripeEmbeddedCheckout } from "@stripe/stripe-js";
import "./payment.scss";

declare const process: {
  env: {
    PAYMENT_HOST?: string;
    FLYWIRE_PORTAL_ENV?: string;
    FLYWIRE_PORTAL_CODE?: string;
  };
};

interface IPaymentData {
  portalCode: string;
  portalSystem?: string;
  concept: string;
  amount: string | number;
  amountDebt?: string | number;
  currency: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerZip?: string;
  customerCountry?: string;
  details: {
    returnUrl?: string;
    travelDate?: string;
    lines?: {
      key: string;
      name: string;
      price: string | number;
      quantity: string | number;
      total: string | number;
    }[];
  };
}

type PayFunction = (data: IPaymentData, container?: string | HTMLElement) => Promise<any>;

declare global {
  interface Window {
    FlywirePayment: any;
    payByStripe?: PayFunction;
    payByFlywire?: PayFunction;
    lastSessionCheckout?: StripeEmbeddedCheckout;
  }
}

const PAYMENT_HOST = process.env.PAYMENT_HOST;

let flywireLoadingPromise: Promise<void> | null = null;

function loadCss() {
  if (document.getElementById("vpay-sdk-css")) {
    return;
  }

  const link = document.createElement("link");
  link.id = "vpay-sdk-css";
  link.rel = "stylesheet";
  link.href = `${PAYMENT_HOST}/payment.min.css`;
  document.head.appendChild(link);
}

function loadFlywireScript(): Promise<void> {
  if (typeof window !== "undefined" && window.FlywirePayment) {
    return Promise.resolve();
  }

  if (flywireLoadingPromise) {
    return flywireLoadingPromise;
  }

  flywireLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById("vtc-flywire-js") as HTMLScriptElement | null;
    if (existingScript) {
      if (window.FlywirePayment) {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Flywire SDK script")));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.flywire.com/flywire-payment.js";
    script.id = "vtc-flywire-js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Flywire SDK script"));
    document.body.appendChild(script);
  });

  return flywireLoadingPromise;
}

async function extractErrorMessage(response: Response, defaultMessage: string): Promise<string> {
  try {
    const data = await response.json();
    if (data) {
      if (typeof data.error === "string") return data.error;
      if (typeof data.detail === "string") return data.detail;
      if (typeof data.message === "string") return data.message;

      // DRF field validation error dictionary
      const keys = Object.keys(data);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const val = data[firstKey];
        if (Array.isArray(val) && val.length > 0) {
          return `${firstKey}: ${val[0]}`;
        }
        if (typeof val === "string") {
          return `${firstKey}: ${val}`;
        }
      }
    }
  } catch {
    // Non-JSON response
  }
  return `${defaultMessage} (HTTP ${response.status})`;
}

function validateContainer(container: string | HTMLElement): HTMLElement {
  if (typeof container === "string") {
    const element = document.getElementById(container);
    if (!element) {
      throw new Error(`Container element with id "${container}" not found`);
    }
    return element;
  }
  return container;
}

async function destroyStripeCheckout() {
  if (window.lastSessionCheckout) {
    window.lastSessionCheckout.destroy();
    window.lastSessionCheckout = undefined;
    // Wait for the checkout to be fully destroyed
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

function createStripeModal(): HTMLElement {
  // Remove previous modal if exists
  const existingBackdrop = document.getElementById("stripe-modal-backdrop");
  if (existingBackdrop) {
    existingBackdrop.remove();
  }

  // Crear modal backdrop
  const modalBackdrop = document.createElement("div");
  modalBackdrop.id = "stripe-modal-backdrop";

  // Crear modal content
  const modalContent = document.createElement("div");
  modalContent.className = "modal-content";

  // Crear botón de cierre
  const closeButton = document.createElement("button");
  closeButton.innerHTML = "×";
  closeButton.className = "close-button";
  closeButton.onclick = async () => {
    await destroyStripeCheckout();
    modalBackdrop.remove();
  };

  // Close modal when clicking on the backdrop
  modalBackdrop.onclick = async (e) => {
    if (e.target === modalBackdrop) {
      await destroyStripeCheckout();
      modalBackdrop.remove();
    }
  };

  // Crear contenedor para Stripe
  const stripeContainer = document.createElement("div");
  stripeContainer.id = "stripe-checkout-container";

  // Crear spinner de carga
  const spinner = document.createElement("div");
  spinner.className = "spinner";

  // Agregar elementos al DOM
  stripeContainer.appendChild(spinner);

  // Agregar el botón de cierre y el contenedor de Stripe al modal
  modalContent.appendChild(closeButton);
  modalContent.appendChild(stripeContainer);
  modalBackdrop.appendChild(modalContent);

  document.body.appendChild(modalBackdrop);
  return stripeContainer;
}

async function payByStripe(data: IPaymentData, container?: string | HTMLElement) {
  if (container) {
    container = validateContainer(container);
  } else {
    // Crear modal para Stripe
    container = createStripeModal();
  }

  data["portalSystem"] = "stripe";

  if (!data.details) {
    data.details = {};
  }

  const host = PAYMENT_HOST || (typeof window !== "undefined" ? window.location.origin : "");
  if (!data.details.returnUrl) {
    data.details.returnUrl = `${host}/payment/pay/{{invoice}}/status`;
  } else if (data.details.returnUrl.startsWith("/")) {
    data.details.returnUrl = `${host}${data.details.returnUrl}`;
  }

  const response = await fetch(`${PAYMENT_HOST}/api/pay`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorMsg = await extractErrorMessage(response, "Payment processing failed");
    throw new Error(errorMsg);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error || "Payment initialization failed");
  }

  const stripe = await loadStripe(result.publishableKey);

  if (!stripe) {
    throw new Error("Failed to load Stripe.js");
  }

  await destroyStripeCheckout();

  const clientSecret = result.clientSecret;

  if (!clientSecret) {
    throw new Error("Missing Stripe client secret in /api/pay response");
  }

  const session = await stripe.initEmbeddedCheckout({
    clientSecret,
  });

  container.replaceChildren(); // Clear spinner or previous content
  session.mount(container);

  window.lastSessionCheckout = session;
}

async function payByFlywire(data: IPaymentData) {
  await loadFlywireScript();

  if (!window.FlywirePayment) {
    throw new Error("Flywire payment SDK is not available");
  }

  data["portalSystem"] = "flywire";

  if (!data.details) {
    data.details = {};
  }

  const response = await fetch(`${PAYMENT_HOST}/api/pay`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorMsg = await extractErrorMessage(response, "Payment processing failed");
    throw new Error(errorMsg);
  }

  const result = await response.json();

  const baseAmount = parseFloat(String(result.data?.amount || 0));
  const feeAmount = parseFloat(String(result.data?.fee || 0));
  const totalAmount = (isNaN(baseAmount) ? 0 : baseAmount) + (isNaN(feeAmount) ? 0 : feeAmount);

  const config = {
    env: process.env.FLYWIRE_PORTAL_ENV || "demo",
    recipientCode: process.env.FLYWIRE_PORTAL_CODE || "VTR",

    amount: totalAmount,

    onInvalidInput(errors: any[]) {
      errors.forEach(function (error) {
        console.error(error.msg);
      });
    },

    requestPayerInfo: true,
    requestRecipientInfo: true,
    skipCompletedSteps: true,

    firstName: data.customerFirstName,
    lastName: data.customerLastName,
    email: data.customerEmail,
    phone: data.customerPhone,
    address: data.customerAddress,
    city: data.customerCity,
    state: data.customerState,
    zip: data.customerZip,
    country: data.customerCountry,

    recipientFields: {
      trip_name: data.concept || "Payment for service",
      booking_number: result.data?.id,
      additional_comments: result.data?.notes,
    },

    paymentOptionsConfig: {
      filters: {
        type: ["online", "credit_card"],
      },
    },

    callbackId: result.paymentRef,
    callbackUrl: `${PAYMENT_HOST}/api/webhooks/flywire`,
    callbackVersion: "2",

    onCompleteCallback(args: any) {
      const { reference, status } = args;

      if (status == "success") {
        let returnUrl = data.details?.returnUrl;

        if (returnUrl) {
          returnUrl = returnUrl.replace("{{invoice}}", result.paymentRef);
        }

        const host = PAYMENT_HOST || window.location.origin;
        window.location.href =
          returnUrl || `${host}/payment/pay/${result.paymentRef}/status`;
      }
    },
  };

  const modal = window.FlywirePayment?.initiate(config);
  modal?.render();
}

function onLoad() {
  loadCss();
  loadFlywireScript().catch((err) => console.error("Flywire script pre-load failed", err));
  window.payByStripe = payByStripe;
  window.payByFlywire = payByFlywire;
}

// Execute the script when the page loads
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", onLoad);
} else {
  onLoad();
}
